import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, SHIPPING_NOTICE, CONTACT_INFO, SHIPPING_INFO, COUNTRY_OF_ORIGIN, BRAND_INFO } from '../data/mock';
import { Truck, Clock, Globe, Package, Phone, Mail, Sparkles, MapPin, Shield, CheckCircle } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const ShippingPage = () => {
  usePageTitle('Shipping Information', 'Free shipping on orders over $99. Standard and express shipping options available within the USA only at Stitch Your Dash.');
  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero Banner */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={siteImages.shopBanner}
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/95 to-warmBrown-800/80" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full text-rose-300 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Delivery Info
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">Shipping Information</h1>
              <p className="text-warmBrown-300 text-lg">Fast & reliable delivery to your door.</p>
            </div>
          </div>
        </section>

        {/* Promo Banner */}
        <section className="bg-gradient-to-r from-rose-500 via-rose-600 to-rose-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <p className="text-center text-white text-sm font-medium flex items-center justify-center gap-2">
              <Truck className="w-4 h-4" />
              {SHIPPING_NOTICE} | Free Shipping on Orders $99+ | All prices in USD
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Country of Origin Notice */}
            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-rose-500" />
                </div>
                <div>
                  <p className="font-semibold text-warmBrown-900">Country of Origin</p>
                  <p className="text-warmBrown-600">{BRAND_INFO.name} is based in the {COUNTRY_OF_ORIGIN}.</p>
                </div>
              </div>
            </div>

            {/* Domestic Shipping Options */}
            <h2 className="font-display text-2xl text-warmBrown-900 mb-6">Shipping Options (United States Only)</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mb-6">
                  <Truck className="w-7 h-7 text-rose-500" />
                </div>
                <h3 className="font-display text-xl text-warmBrown-900 mb-2">{SHIPPING_INFO.domestic.standard.name}</h3>
                <p className="text-3xl font-bold text-rose-600 mb-4">${SHIPPING_INFO.domestic.standard.price}</p>
                <p className="text-warmBrown-600 text-sm mb-2">Free on orders over ${SHIPPING_INFO.domestic.standard.freeThreshold}</p>
                <div className="space-y-2 text-sm text-warmBrown-500">
                  <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> {SHIPPING_INFO.domestic.standard.deliveryTime}</p>
                  <p className="flex items-center gap-2"><Package className="w-4 h-4" /> {SHIPPING_INFO.domestic.standard.carrier}</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                <div className="w-14 h-14 bg-sage-100 rounded-2xl flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-sage-600" />
                </div>
                <h3 className="font-display text-xl text-warmBrown-900 mb-2">{SHIPPING_INFO.domestic.express.name}</h3>
                <p className="text-3xl font-bold text-rose-600 mb-4">${SHIPPING_INFO.domestic.express.price}</p>
                <p className="text-warmBrown-600 text-sm mb-2">Priority delivery</p>
                <div className="space-y-2 text-sm text-warmBrown-500">
                  <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> {SHIPPING_INFO.domestic.express.deliveryTime}</p>
                  <p className="flex items-center gap-2"><Package className="w-4 h-4" /> {SHIPPING_INFO.domestic.express.carrier}</p>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-rose-500" />
                  <h3 className="font-semibold text-warmBrown-900 text-lg">Shipping Region</h3>
                </div>
                <p className="text-warmBrown-600 leading-relaxed">
                  We currently ship to all 50 states within the <strong>United States</strong> only.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                <div className="flex items-center gap-3 mb-4">
                  <Package className="w-6 h-6 text-rose-500" />
                  <h3 className="font-semibold text-warmBrown-900 text-lg">Processing Time</h3>
                </div>
                <p className="text-warmBrown-600 leading-relaxed">
                  {SHIPPING_INFO.processingTime}
                </p>
              </div>
            </div>

            {/* Shipping Carriers */}
            <div className="bg-cream-100 rounded-3xl p-8 mb-12">
              <h3 className="font-display text-xl text-warmBrown-900 mb-4">Our Shipping Partners</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 bg-white p-4 rounded-2xl">
                  <CheckCircle className="w-5 h-5 text-rose-500" />
                  <span className="text-warmBrown-700 font-medium">USPS</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-4 rounded-2xl">
                  <CheckCircle className="w-5 h-5 text-rose-500" />
                  <span className="text-warmBrown-700 font-medium">UPS (select items)</span>
                </div>
                <div className="flex items-center gap-3 bg-white p-4 rounded-2xl">
                  <CheckCircle className="w-5 h-5 text-rose-500" />
                  <span className="text-warmBrown-700 font-medium">FedEx (select items)</span>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-warmBrown-800 rounded-3xl p-8 text-center">
              <h3 className="font-display text-xl text-white mb-4">Questions About Shipping?</h3>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-2 text-rose-300 hover:text-rose-200 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {CONTACT_INFO.phone}
                </a>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2 text-rose-300 hover:text-rose-200 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  {CONTACT_INFO.email}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ShippingPage;
